// Business-domain API client. Naming convention: snake_case wire types,
// camelCase request bodies (matches the API).

import { api } from "./api";

// ─── Customers / Suppliers ─────────────────────────────────────────────
export interface Customer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  vatRegistrationNo?: string | null;
  tin?: string | null;
  billingAddress: string | null;
  isExport?: boolean;
  preferredLanguage: string | null;
  notes: string | null;
}

export interface CustomerInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  vatRegistrationNo?: string | null;
  tin?: string | null;
  billingAddress?: string | null;
  isExport?: boolean;
  preferredLanguage?: "en" | "si" | "ta" | null;
  notes?: string | null;
}

export const customersApi = {
  list: (q?: string) =>
    api.get<{ customers: Customer[] }>(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  get: (id: string) => api.get<Customer>(`/customers/${id}`),
  create: (body: CustomerInput) => api.post<Customer>("/customers", body),
  update: (id: string, body: Partial<CustomerInput>) =>
    api.patch<Customer>(`/customers/${id}`, body),
  remove: (id: string) => api.delete<void>(`/customers/${id}`),
};

export interface Supplier {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  vatRegistrationNo?: string | null;
  address: string | null;
  // Default expense account from the CoA. When set, new bills for this
  // supplier auto-pick this as the expense account.
  defaultExpenseAccountId?: string | null;
  // WHT — drives default rate on bills + the per-supplier certificate.
  isResident?: boolean;
  isIndividual?: boolean;
  whtCategoryCode?: string | null;
  tin?: string | null;
  nicOrPassport?: string | null;
}

export interface SupplierInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  vatRegistrationNo?: string | null;
  address?: string | null;
  defaultExpenseAccountId?: string | null;
  isResident?: boolean;
  isIndividual?: boolean;
  whtCategoryCode?: string | null;
  tin?: string | null;
  nicOrPassport?: string | null;
}

export const suppliersApi = {
  list: (q?: string) =>
    api.get<{ suppliers: Supplier[] }>(`/suppliers${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  get: (id: string) => api.get<Supplier>(`/suppliers/${id}`),
  create: (body: SupplierInput) => api.post<Supplier>("/suppliers", body),
  update: (id: string, body: Partial<SupplierInput>) =>
    api.patch<Supplier>(`/suppliers/${id}`, body),
  remove: (id: string) => api.delete<void>(`/suppliers/${id}`),
};

// ─── WHT helpers ──────────────────────────────────────────────────────────
export interface WhtRule {
  code: string;
  name: string;
  rate_pct: string;
  ird_reference: string | null;
  trigger_type: string;
  applies_to: string[];
}

export interface WhtCertificate {
  supplier: {
    id: string;
    name: string;
    tin: string | null;
    nic_or_passport: string | null;
    is_resident: boolean;
    is_individual: boolean;
    wht_category_code: string | null;
    address: string | null;
  };
  period: { year: number; start: string; end: string };
  months: Array<{
    month: number;
    gross_cents: string;
    wht_cents: string;
    bills: Array<{
      id: string;
      bill_number: string;
      issue_date: string;
      gross_cents: string;
      wht_cents: string;
      lines: Array<{
        description: string;
        gross_cents: string;
        wht_rate_pct: string | null;
        wht_cents: string;
      }>;
    }>;
  }>;
  totals: { gross_cents: string; wht_cents: string };
}

export const whtApi = {
  rules: () => api.get<{ rules: WhtRule[] }>("/wht/rules"),
  thresholdCheck: (supplierId: string, year: number, month: number) =>
    api.get<{
      supplier_id: string;
      year: number;
      month: number;
      month_to_date_cents: string;
      threshold_cents: string;
      crossed: boolean;
      exceeds_by_cents: string;
      remaining_headroom_cents: string;
    }>(`/wht/threshold-check?supplierId=${supplierId}&year=${year}&month=${month}`),
  certificate: (supplierId: string, year: number) =>
    api.get<WhtCertificate>(`/wht/certificate?supplierId=${supplierId}&year=${year}`),
};

// ─── Invoices ──────────────────────────────────────────────────────────
export type InvoiceStatus = "draft" | "sent" | "partially_paid" | "paid" | "overdue" | "void";

export interface InvoiceListItem {
  id: string;
  invoice_number: string;
  customer: { id: string; name: string };
  issue_date: string;
  due_date: string;
  currency: string;
  status: InvoiceStatus;
  is_template: boolean;
  subtotal_cents: string;
  total_cents: string;
  paid_cents: string;
  reference: string | null;
  public_token: string | null;
}

export interface InvoiceLine {
  id: string;
  position: number;
  description: string;
  line_type: string;
  quantity: string;
  unit_price_cents: string;
  line_subtotal_cents: string;
  vat_classification: string | null;
  vat_rate_pct: string | null;
  vat_amount_cents: string;
  sscl_amount_cents: string;
  wht_rate_pct: string | null;
  wht_amount_cents: string;
  account_id: string | null;
}

export interface Invoice extends InvoiceListItem {
  customer: Customer & { id: string; name: string };
  notes: string | null;
  sent_via: string[];
  sent_at: string | null;
  vat_total_cents: string;
  sscl_total_cents: string;
  wht_total_cents: string;
  lines: InvoiceLine[];
}

export interface InvoiceLineInput {
  description: string;
  lineType: "goods" | "service";
  quantity: number;
  unitPriceCents: number | string;
  vatClassification?:
    | "taxable"
    | "zero_rated_goods"
    | "zero_rated_services"
    | "exempt"
    | "excluded"
    | "deemed_specified"
    | "deemed_sdp"
    | null;
  vatRatePct?: number | null;
  ssclApply?: boolean;
  whtRatePct?: number | null;
}

export interface InvoiceCreateInput {
  customerId: string;
  reference?: string | null;
  issueDate: string;
  dueDate: string;
  currency?: string;
  notes?: string | null;
  lines: InvoiceLineInput[];
}

function genIdempotencyKey(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const invoicesApi = {
  list: (status?: string, customerId?: string) => {
    const q = new URLSearchParams();
    if (status) q.set("status", status);
    if (customerId) q.set("customer_id", customerId);
    const qs = q.toString();
    return api.get<{ invoices: InvoiceListItem[] }>(
      `/invoices${qs ? `?${qs}` : ""}`
    );
  },
  get: (id: string) => api.get<Invoice>(`/invoices/${id}`),
  create: (body: InvoiceCreateInput) =>
    api.post<Invoice>("/invoices", body, { "Idempotency-Key": genIdempotencyKey() }),
  update: (id: string, body: InvoiceCreateInput) =>
    api.patch<Invoice>(`/invoices/${id}`, body),
  send: (id: string, channels: string[] = ["whatsapp", "email"]) =>
    api.post<Invoice>(`/invoices/${id}/send`, { channels }),
  void: (id: string) => api.post<{ id: string; status: string }>(`/invoices/${id}/void`),
  pdfUrl: (id: string) => `/clearbooks/api/invoices/${id}/pdf`,
  // Recurring templates: a "template" is a draft invoice flagged so it
  // doesn't show in the main list. `clone` creates a fresh draft populated
  // with the template's customer + lines.
  listTemplates: () =>
    api.get<{ invoices: InvoiceListItem[] }>("/invoices?templates=1"),
  markTemplate: (id: string, isTemplate: boolean) =>
    api.post<{ id: string; is_template: boolean }>(`/invoices/${id}/mark-template`, { isTemplate }),
  clone: (id: string) => api.post<Invoice>(`/invoices/${id}/clone`),
};

// ─── Bills ─────────────────────────────────────────────────────────────
export type BillStatus =
  | "draft"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "partially_paid"
  | "paid"
  | "void";

export interface BillListItem {
  id: string;
  bill_number: string;
  supplier: { id: string; name: string };
  issue_date: string;
  due_date: string;
  status: BillStatus;
  total_cents: string;
  paid_cents: string;
}

export interface Bill extends BillListItem {
  currency: string;
  notes: string | null;
  reference: string | null;
  tax_invoice_received_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  receipt_id: string | null;
  subtotal_cents: string;
  vat_total_cents: string;
  sscl_total_cents: string;
  wht_total_cents: string;
  lines: InvoiceLine[];
}

// BillLineInput overrides vatClassification because the input side has a
// different set of valid values than the output side.
export interface BillLineInput extends Omit<InvoiceLineInput, "vatClassification"> {
  vatClassification?: "taxable" | "imported" | "non_vat_vendor" | "exempt" | "excluded" | null;
}
export interface BillCreateInput {
  supplierId: string;
  billNumber: string;
  reference?: string | null;
  issueDate: string;
  dueDate: string;
  currency?: string;
  notes?: string | null;
  taxInvoiceReceived?: boolean;
  receiptId?: string | null;
  lines: BillLineInput[];
}

export const billsApi = {
  list: (status?: string, supplierId?: string) => {
    const q = new URLSearchParams();
    if (status) q.set("status", status);
    if (supplierId) q.set("supplier_id", supplierId);
    const qs = q.toString();
    return api.get<{ bills: BillListItem[] }>(`/bills${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api.get<Bill>(`/bills/${id}`),
  create: (body: BillCreateInput) =>
    api.post<Bill>("/bills", body, { "Idempotency-Key": genIdempotencyKey() }),
  update: (id: string, body: BillCreateInput) =>
    api.patch<Bill>(`/bills/${id}`, body),
  approve: (id: string) => api.post<{ id: string; status: BillStatus }>(`/bills/${id}/approve`),
  reject: (id: string, reason: string) =>
    api.post<{ id: string; status: BillStatus }>(`/bills/${id}/reject`, { reason }),
  void: (id: string) => api.post<{ id: string; status: BillStatus }>(`/bills/${id}/void`),
};

// ─── Payments ──────────────────────────────────────────────────────────
export interface PaymentInput {
  direction: "in" | "out";
  paymentDate: string;
  amountCents: string | number;
  currency?: string;
  bankAccountId?: string | null;
  reference?: string | null;
  notes?: string | null;
  invoiceId?: string | null;
  billId?: string | null;
}

export const paymentsApi = {
  create: (body: PaymentInput) =>
    api.post<{ id: string; payment_date: string; amount_cents: string }>(
      "/payments",
      body,
      { "Idempotency-Key": genIdempotencyKey() }
    ),
  list: (params?: { invoiceId?: string; billId?: string }) => {
    const q = new URLSearchParams();
    if (params?.invoiceId) q.set("invoice_id", params.invoiceId);
    if (params?.billId) q.set("bill_id", params.billId);
    const qs = q.toString();
    return api.get<{ payments: Array<{ id: string; direction: string; payment_date: string; amount_cents: string; invoice_id: string | null; bill_id: string | null; reference: string | null; bank_account_id: string | null }> }>(
      qs ? `/payments?${qs}` : "/payments",
    );
  },
  remove: (id: string) => api.delete<{ id: string; reversed: boolean }>(`/payments/${id}`),
};

// ─── History (audit timeline for one document) ─────────────────────────
export interface HistoryEvent {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  occurred_at: string;
  user_email: string | null;
  user_name: string | null;
  before: unknown;
  after: unknown;
  diff: unknown;
}
export const historyApi = {
  list: (entityType: string, entityId: string) =>
    api.get<{ events: HistoryEvent[] }>(`/history/${entityType}/${entityId}`),
};

// ─── Attachments (polymorphic file uploads) ────────────────────────────
export interface Attachment {
  id: string;
  entity_type: string;
  entity_id: string;
  filename: string;
  mime_type: string;
  size_bytes: string;
  uploaded_at: string;
  uploaded_by: string | null;
  download_url: string;
}
export const attachmentsApi = {
  list: (entityType: string, entityId: string) =>
    api.get<{ attachments: Attachment[] }>(`/attachments?entity_type=${entityType}&entity_id=${entityId}`),
  upload: (entityType: string, entityId: string, file: File) =>
    api.postFile<Attachment>(`/attachments?entity_type=${entityType}&entity_id=${entityId}`, file),
  remove: (id: string) => api.delete<{ id: string; removed: boolean }>(`/attachments/${id}`),
};

// ─── AI Inbox — Scryb-equivalent doc-to-entry pipeline ───────────────
// Phase 1a: bills only. Backend runs Claude synchronously and returns the
// extracted item ready to review.
export interface ExtractedBillLine {
  description: string;
  quantity: number;
  unit_price_cents: string;
  line_subtotal_cents: string;
  vat_rate_pct: number | null;
  vat_amount_cents: string | null;
  _confidence: number;
}
export interface ExtractedBill {
  supplier: {
    name: string | null;
    vat_no: string | null;
    tin: string | null;
    address: string | null;
    _confidence: number;
  };
  invoice_number: string | null;
  issue_date: string | null;
  due_date: string | null;
  currency: string | null;
  lines: ExtractedBillLine[];
  subtotal_cents: string | null;
  vat_total_cents: string | null;
  wht_total_cents: string | null;
  total_cents: string | null;
  notes: string | null;
  document_language: string | null;
  handwritten: boolean;
  overall_confidence: number;
}
export interface InboxItem {
  id: string;
  source_kind: "bill" | "invoice" | "receipt" | "bank_statement";
  status: "pending" | "extracting" | "needs_review" | "posted" | "rejected" | "failed";
  // Shape depends on source_kind — cast to ExtractedBill / ExtractedBankStatement /
  // ExtractedReceipt at the call site.
  extracted_json: ExtractedBill | ExtractedBankStatement | ExtractedReceipt | null;
  extract_error: string | null;
  attachment_id: string;
  posted_entity_type: string | null;
  posted_entity_id: string | null;
  posted_at: string | null;
  created_at: string;
  updated_at: string;
}
export interface InboxDetail extends InboxItem {
  attachment: {
    id: string;
    filename: string;
    mime_type: string;
    size_bytes: string;
    download_url: string;
  } | null;
}
export interface ExtractedBankTxn {
  date: string;
  description: string;
  reference: string | null;
  amount_cents: string;
  balance_cents: string | null;
  _confidence: number;
}
export interface ExtractedBankStatement {
  bank_name: string | null;
  account_number: string | null;
  account_holder: string | null;
  currency: string | null;
  period_start: string | null;
  period_end: string | null;
  opening_balance_cents: string | null;
  closing_balance_cents: string | null;
  transactions: ExtractedBankTxn[];
  document_language: string | null;
  handwritten: boolean;
  overall_confidence: number;
}
export interface ExtractedReceipt {
  merchant: { name: string | null; address: string | null; _confidence: number };
  receipt_date: string | null;
  currency: string | null;
  total_cents: string | null;
  vat_amount_cents: string | null;
  payment_method: string | null;
  category_hint: string | null;
  description: string | null;
  document_language: string | null;
  handwritten: boolean;
  overall_confidence: number;
}
export type InboxKind = "bill" | "bank_statement" | "receipt";

export const inboxApi = {
  list: (status?: string) => {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    return api.get<{ items: InboxItem[] }>(`/inbox${q}`);
  },
  get: (id: string) => api.get<InboxDetail>(`/inbox/${id}`),
  upload: (file: File, kind: InboxKind = "bill") =>
    api.postFile<InboxItem>(`/inbox/upload?kind=${kind}`, file),
  approve: (id: string, override?: Record<string, unknown>) =>
    api.post<{
      inbox: InboxItem;
      bill?: { id: string; bill_number: string; status: string };
      bank_statement?: { id: string; transactions: number; auto_matched: number };
      payment?: { id: string; amount_cents: string };
    }>(`/inbox/${id}/approve`, override ?? {}),
  reject: (id: string) => api.post<InboxItem>(`/inbox/${id}/reject`, {}),
  reextract: (id: string) => api.post<InboxItem>(`/inbox/${id}/reextract`, {}),
};

// ─── Bank ──────────────────────────────────────────────────────────────
export interface BankAccount {
  id: string;
  bankCode: string;
  accountName: string;
  accountNumber: string;
  currency: string;
}

export interface BankTransaction {
  id: string;
  statement_id: string;
  transaction_date: string;
  description: string;
  amount_cents: string;
  balance_cents: string | null;
  reference: string | null;
  match_status: "unmatched" | "suggested" | "approved" | "manual";
  match_confidence: string | null;
  matched_entry_id: string | null;
}

export const bankApi = {
  listAccounts: () => api.get<{ accounts: BankAccount[] }>("/bank/accounts"),
  createAccount: (body: {
    bankCode: string;
    accountName: string;
    accountNumber: string;
    currency?: string;
  }) => api.post<BankAccount>("/bank/accounts", body),
  uploadStatement: async (bankAccountId: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("bankAccountId", bankAccountId);
    const res = await fetch("/clearbooks/api/bank/statements", {
      method: "POST",
      headers: bearer(),
      body: fd,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? body.parse_error ?? `upload failed ${res.status}`);
    }
    return res.json() as Promise<{
      id: string;
      parse_status: string;
      transactions: number;
      period: { from: string; to: string };
    }>;
  },
  listTransactions: (params: { match_status?: string; statement_id?: string } = {}) => {
    const q = new URLSearchParams(params as Record<string, string>);
    return api.get<{ transactions: BankTransaction[] }>(
      `/bank/transactions${q.toString() ? `?${q}` : ""}`
    );
  },
  match: (txnId: string, kind: "invoice" | "bill", id: string) =>
    api.post<{ id: string; match_status: string }>(`/bank/transactions/${txnId}/match`, {
      kind,
      id,
    }),
  bulkApprove: (minConfidence = 99) =>
    api.post<{ approved: number }>("/bank/transactions/bulk-approve", {
      min_confidence: minConfidence,
    }),
};

// ─── VAT return ────────────────────────────────────────────────────────
export interface VatReturn {
  period: { start: string; end: string };
  vat_rate_pct: number;
  cages: Array<{ code: string; label: string; value_cents: string }>;
  schedules: {
    sch_01_output: Array<Record<string, unknown>>;
    sch_02_zero_rated: Array<Record<string, unknown>>;
    sch_03_exempt: Array<Record<string, unknown>>;
    sch_04_deemed: Array<Record<string, unknown>>;
    sch_05_imports: Array<Record<string, unknown>>;
    sch_06_local_purchases: Array<Record<string, unknown>>;
    sch_07_non_vat: Array<Record<string, unknown>>;
  };
  summary: {
    total_output_tax_cents: string;
    total_input_tax_cents: string;
    net_payable_cents: string;
  };
}

export interface VatReconciliation {
  period: { start: string; end: string };
  reconciled: boolean;
  rows: Array<{
    label: string;
    cage: string | null;
    return_cents: string;
    ledger_cents: string;
    difference_cents: string;
    reconciled: boolean;
  }>;
}

export const vatApi = {
  getReturn: (start: string, end: string) =>
    api.get<VatReturn>(`/vat/return?period_start=${start}&period_end=${end}`),
  reconciliation: (start: string, end: string) =>
    api.get<VatReconciliation>(`/vat/reconciliation?period_start=${start}&period_end=${end}`),
};

// ─── SSCL ──────────────────────────────────────────────────────────────
export interface SsclReturn {
  period: { start: string; end: string };
  tenant_default_sector: string;
  rate_pct: number;
  sector_liable_pct: Record<string, number>;
  months: Array<{
    label: string;
    month_index: number;
    gross_total_cents: string;
    gross_by_sector: Record<string, string>;
    liable_by_sector: Record<string, string>;
    sscl_payable_cents: string;
  }>;
  summary: { total_gross_cents: string; total_sscl_cents: string };
  installments: Array<{ label: string; amount_cents: string }>;
}
export interface SsclRegistrationCheck {
  as_of: string;
  thresholds: {
    single_quarter_cents: string;
    four_quarter_cents: string;
    effective_from: string;
  };
  tenant_is_sscl_liable: boolean;
  quarters: Array<{ index: number; liable_turnover_cents: string; exceeded_single_qtr: boolean }>;
  four_quarter_total_cents: string;
  exceeded_single_qtr_in_window: boolean;
  exceeded_four_qtr_in_window: boolean;
  should_register: boolean;
}

export const ssclApi = {
  getReturn: (start: string, end: string) =>
    api.get<SsclReturn>(`/sscl/return?period_start=${start}&period_end=${end}`),
  registrationCheck: () => api.get<SsclRegistrationCheck>("/sscl/registration-check"),
};

// ─── EPF + ETF ────────────────────────────────────────────────────────
export interface EpfFormC {
  header: {
    epf_registration_no: string | null;
    contribution_period: string;
    cheque_no: string | null;
    bank: string | null;
    branch: string | null;
    total_remittance_cents: string;
  };
  rows: Array<{
    employee_name: string;
    nic: string | null;
    member_no: string | null;
    total_earnings_cents: string;
    employer_share_cents: string;
    employee_share_cents: string;
    total_contribution_cents: string;
  }>;
  summary: {
    member_count: number;
    total_earnings_cents: string;
    employer_share_cents: string;
    employee_share_cents: string;
    total_remittance_cents: string;
  };
  payee: string;
}
export interface EtfFormII {
  header: { etf_registration_no: string | null; period: string; months: number[] };
  form_ii: {
    members: Array<{
      member_name: string;
      member_no: string | null;
      nic: string | null;
      total_contribution_cents: string;
      months: Array<{ month: number; earnings_cents: string; contribution_cents: string }>;
    }>;
    member_count: number;
  };
  reconciliation: Array<{
    month: number;
    payable_cents: string;
    remitted_cents: string;
    cheque_no: string | null;
    bank: string | null;
    payment_date: string | null;
    variance_cents: string;
  }>;
  summary: {
    grand_total_contributions_cents: string;
    grand_total_remitted_cents: string;
    reconciles: boolean;
  };
}
export const payrollApi = {
  epfFormC: (year: number, month: number) =>
    api.get<EpfFormC>(`/payroll/epf/form-c?year=${year}&month=${month}`),
  etfFormII: (year: number, half: 1 | 2) =>
    api.get<EtfFormII>(`/payroll/etf/form-ii?year=${year}&half=${half}`),
  // Run management
  listRuns: () => api.get<{ runs: PayrollRun[] }>(`/payroll/runs`),
  createRun: (year: number, month: number) =>
    api.post<{ id: string; status: string }>(`/payroll/runs`, { year, month }),
  computeRun: (id: string) =>
    api.post<{ ok: boolean; entries: number; total_gross_cents: string; total_net_cents: string }>(`/payroll/runs/${id}/compute`, {}),
  postRun: (id: string) =>
    api.post<{ ok: boolean; ledger_entry_id: string }>(`/payroll/runs/${id}/post`, {}),
  getRun: (id: string) => api.get<PayrollRunDetail>(`/payroll/runs/${id}`),
};

export interface PayrollRun {
  id: string;
  year: number;
  month: number;
  status: string;
  staff_count: number;
  total_gross_cents: string;
  total_net_cents: string;
  total_apit_cents: string;
  total_epf_employee_cents: string;
  total_epf_employer_cents: string;
  total_etf_employer_cents: string;
  computed_at: string | null;
  posted_at: string | null;
  ledger_entry_id: string | null;
}

export interface PayrollRunDetail extends Omit<PayrollRun,
  "total_gross_cents" | "total_net_cents" | "total_apit_cents" |
  "total_epf_employee_cents" | "total_epf_employer_cents" | "total_etf_employer_cents"
> {
  totals: {
    gross_cents: string;
    net_cents: string;
    apit_cents: string;
    epf_employee_cents: string;
    epf_employer_cents: string;
    etf_employer_cents: string;
  };
  entries: Array<{
    id: string;
    employee_id: string;
    employee_name: string;
    emp_no: string;
    gross_cents: string;
    ncb_cents: string;
    ncb_breakdown: Array<{ code: string; name: string; value_cents: string; explanation: string }>;
    epf_employee_cents: string;
    epf_employer_cents: string;
    etf_employer_cents: string;
    apit_cents: string;
    apit_table: string | null;
    apit_provisional: boolean;
    net_pay_cents: string;
  }>;
}

// ─── Employees ─────────────────────────────────────────────────────────
export interface Employee {
  id: string;
  emp_no: string;
  full_name: string;
  nic: string | null;
  email: string | null;
  phone: string | null;
  tin: string | null;
  epf_member_no: string | null;
  etf_member_no: string | null;
  base_salary_cents: string;
  bank_name: string | null;
  bank_branch: string | null;
  bank_account_no: string | null;
  joined_at: string | null;
  left_at: string | null;
  is_active: boolean;
}

export interface EmployeeBenefitRow {
  id: string;
  benefit_type_id: string;
  benefit_type_code: string;
  benefit_type_name: string;
  formula: string;
  cost_cents: string | null;
  km_this_month: string | null;
  override_value_cents: string | null;
  notes: string | null;
  effective_from: string;
  effective_to: string | null;
}

export interface EmployeeInput {
  empNo: string;
  fullName: string;
  nic?: string | null;
  email?: string | null;
  phone?: string | null;
  tin?: string | null;
  epfMemberNo?: string | null;
  etfMemberNo?: string | null;
  baseSalaryCents?: number | string;
  bankName?: string | null;
  bankBranch?: string | null;
  bankAccountNo?: string | null;
  joinedAt?: string | null;
  isActive?: boolean;
}

export const employeesApi = {
  list: (q?: string) => api.get<{ employees: Employee[] }>(`/employees${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  get: (id: string) => api.get<Employee & { benefits: EmployeeBenefitRow[] }>(`/employees/${id}`),
  create: (body: EmployeeInput) => api.post<Employee>(`/employees`, body),
  update: (id: string, body: Partial<EmployeeInput>) => api.patch<Employee>(`/employees/${id}`, body),
  remove: (id: string) => api.delete<void>(`/employees/${id}`),
  addBenefit: (id: string, body: { benefitTypeId: string; costCents?: number | string; kmThisMonth?: number; overrideValueCents?: number | string; notes?: string | null; effectiveFrom?: string }) =>
    api.post<{ id: string }>(`/employees/${id}/benefits`, body),
  removeBenefit: (id: string, benefitId: string) =>
    api.delete<void>(`/employees/${id}/benefits/${benefitId}`),
  importCsv: (csv: string) =>
    api.post<{ created: number; updated: number; errors: Array<{ row: number; message: string }> }>(`/employees/import-csv`, { csv }),
};

export interface NcbType {
  id: string;
  code: string;
  name: string;
  formula: string;
  flat_cents: string | null;
  pct: string | null;
  bundle_cents: Record<string, number> | null;
  per_km_cents: string | null;
  cap_cents: string | null;
  ird_reference: string | null;
  is_system: boolean;
}

export const ncbApi = {
  types: () => api.get<{ types: NcbType[] }>("/ncb/types"),
  updateType: (id: string, body: {
    flatCents?: string | number | null;
    pct?: number | null;
    bundleCents?: Record<string, number> | null;
    perKmCents?: string | number | null;
    capCents?: string | number | null;
    isActive?: boolean;
  }) => api.patch<{ id: string }>(`/ncb/types/${id}`, body),
};

export const VAT_OUTPUT_CLASSIFICATIONS = [
  { value: "taxable",             label: "Taxable (18%)" },
  { value: "zero_rated_goods",    label: "Zero-rated — goods (export)" },
  { value: "zero_rated_services", label: "Zero-rated — services (export)" },
  { value: "exempt",              label: "Exempt" },
  { value: "excluded",            label: "Excluded" },
  { value: "deemed_specified",    label: "Deemed taxable — specified projects" },
  { value: "deemed_sdp",          label: "Deemed taxable — SDP" },
] as const;

export const VAT_INPUT_CLASSIFICATIONS = [
  { value: "taxable",        label: "Taxable purchase (claim input VAT)" },
  { value: "imported",       label: "Imported goods" },
  { value: "non_vat_vendor", label: "Non-VAT registered vendor" },
  { value: "exempt",         label: "Exempt purchase" },
  { value: "excluded",       label: "Excluded" },
] as const;

// ─── Reports ───────────────────────────────────────────────────────────
export const reportsApi = {
  trialBalance: (asOf?: string) =>
    api.get<{
      as_of: string;
      rows: Array<{ code: string; name: string; type: string; debit_cents: string; credit_cents: string; balance_cents: string }>;
      totals: { debit_cents: string; credit_cents: string };
    }>(`/reports/trial-balance${asOf ? `?as_of=${asOf}` : ""}`),
  profitLoss: (start?: string, end?: string, compare?: "year" | "prior" | "none") => {
    type Body = {
      period: { start: string; end: string };
      revenue: { lines: Array<{ code: string; name: string; amount_cents: string }>; total_cents: string };
      expenses: { lines: Array<{ code: string; name: string; amount_cents: string }>; total_cents: string };
      net_profit_cents: string;
    };
    const params = new URLSearchParams();
    if (start) params.set("period_start", start);
    if (end) params.set("period_end", end);
    if (compare) params.set("compare", compare);
    const qs = params.toString();
    return api.get<Body & { compare: Body | null }>(`/reports/profit-loss${qs ? `?${qs}` : ""}`);
  },
  incomeTaxComputation: (start: string, end: string) =>
    api.get<{
      period: { start: string; end: string };
      net_profit_cents: string;
      add_backs: {
        disallowable: { rows: Array<{ code: string; name: string; amount_cents: string }>; total_cents: string };
        partial_review: { rows: Array<{ code: string; name: string; amount_cents: string }>; total_cents: string; note: string };
      };
      adjusted_profit_cents: string;
    }>(`/reports/income-tax-computation?period_start=${start}&period_end=${end}`),
  balanceSheet: (asOf?: string, compare?: "year" | "month" | "none") => {
    type Body = {
      as_of: string;
      assets: { lines: Array<{ code: string; name: string; balance_cents: string }>; total_cents: string };
      liabilities: { lines: Array<{ code: string; name: string; balance_cents: string }>; total_cents: string };
      equity: { lines: Array<{ code: string; name: string; balance_cents: string }>; total_cents: string };
    };
    const params = new URLSearchParams();
    if (asOf) params.set("as_of", asOf);
    if (compare) params.set("compare", compare);
    const qs = params.toString();
    return api.get<Body & { compare: Body | null }>(`/reports/balance-sheet${qs ? `?${qs}` : ""}`);
  },
  arAgeing: (asOf?: string) =>
    api.get<{
      as_of: string;
      buckets: {
        current_cents: string;
        days_1_30_cents: string;
        days_31_60_cents: string;
        days_61_90_cents: string;
        over_90_cents: string;
      };
      total_cents: string;
      invoices: Array<{
        id: string;
        invoice_number: string;
        customer: { id: string; name: string };
        due_date: string;
        days_overdue: number;
        remaining_cents: string;
        bucket: string;
      }>;
    }>(`/reports/ar-ageing${asOf ? `?as_of=${asOf}` : ""}`),
  apAgeing: (asOf?: string) =>
    api.get<{
      as_of: string;
      buckets: {
        current_cents: string;
        days_1_30_cents: string;
        days_31_60_cents: string;
        days_61_90_cents: string;
        over_90_cents: string;
      };
      total_cents: string;
      bills: Array<{
        id: string;
        bill_number: string;
        supplier: { id: string; name: string };
        due_date: string;
        days_overdue: number;
        remaining_cents: string;
        bucket: string;
      }>;
    }>(`/reports/ap-ageing${asOf ? `?as_of=${asOf}` : ""}`),
};

function bearer(): Record<string, string> {
  const token = localStorage.getItem("clearbooks.auth");
  if (!token) return {};
  try {
    const parsed = JSON.parse(token);
    return { Authorization: `Bearer ${parsed.state.accessToken}` };
  } catch {
    return {};
  }
}

// ─── Plans / billing ──────────────────────────────────────────────────
export interface PlanPrice {
  net_cents: string;
  sscl_cents: string;
  vat_cents: string;
  gross_cents: string;
}
export interface PlanCatalogEntry {
  key: "trial" | "starter" | "pro" | "enterprise";
  name: string;
  position: number;
  monthly: PlanPrice | null;
  annual: PlanPrice | null;
  features: {
    payroll: boolean;
    multiCurrency: boolean;
    customBranding: boolean;
    whiteGlove: boolean;
  };
  limits: { max_invoices_per_month: number | null };
  contact_sales: boolean;
}
export interface SubscriptionRow {
  id: string;
  plan: string;
  billing_cycle: "monthly" | "annual";
  current_period_start: string;
  current_period_end: string;
  net_cents: string;
  sscl_cents: string;
  vat_cents: string;
  gross_cents: string;
  status: string;
  cancelled_at: string | null;
  created_at: string;
}

export const plansApi = {
  list: () => api.get<{ plans: PlanCatalogEntry[] }>("/plans"),
};

export interface ReceiptRow {
  id: string;
  invoice_number: string;
  issue_date: string;
  subtotal_cents: string;
  sscl_cents: string;
  vat_cents: string;
  gross_cents: string;
  status: string;
  pdf_url: string;
}

export interface PreviewBreakdown {
  base_plan_net_cents: string;
  extra_seats: number;
  extra_seats_net_cents: string;
  months_prepaid: number;
  discount_pct: number;
  discount_net_cents: string;
  coupon_discount_net_cents?: string;
  subtotal_before_discount_net_cents: string;
  net_cents: string;
  gross_cents: string;
}

export interface PreviewResponse {
  current: {
    plan: string;
    billing_cycle: string;
    seats: number;
    months_prepaid: number;
    current_period_end: string;
    next_renewal: { date: string; net_cents: string; gross_cents: string };
    status: string;
    current_seats?: number;
    paid_seats?: number;
  } | null;
  change: {
    new_plan: string;
    new_cycle: string;
    seats?: number;
    months_prepaid?: number;
    no_op?: boolean;
    days_remaining?: number;
    days_in_period?: number;
    net_delta_cents?: string;
    today_charge: {
      net_cents: string;
      sscl_cents: string;
      vat_cents: string;
      gross_cents: string;
    } | null;
    breakdown?: PreviewBreakdown | null;
    first_full_period: { net_cents: string; gross_cents: string };
  } | null;
  pending_seat_charge?: {
    extra_seats: number;
    today_charge: { net_cents: string; sscl_cents: string; vat_cents: string; gross_cents: string };
  } | null;
  coupon?: {
    code: string;
    discount_pct: number | null;
    discount_fixed_cents: string | null;
  } | null;
  coupon_error?: string | null;
}

export interface PaymentMethodResponse {
  has_method: boolean;
  brand?: string | null;
  last4?: string | null;
  added_at?: string | null;
}

export const billingApi = {
  history: () => api.get<{ current: SubscriptionRow | null; history: SubscriptionRow[] }>("/subscriptions"),
  start: (plan: "starter" | "pro", billing_cycle: "monthly" | "annual") =>
    api.post<{
      intent_id: string;
      checkout_url: string;
      method: "payhere" | "bank_transfer";
      payhere_form: Record<string, string> | null;
      payhere_action: string | null;
      plan: string;
      billing_cycle: string;
      net_cents: string;
      sscl_cents: string;
      vat_cents: string;
      gross_cents: string;
    }>("/subscriptions/start", { plan, billing_cycle }),
  cancel: () =>
    api.post<{ id: string; status: string; current_period_end: string; message: string }>("/subscriptions/cancel"),
  receipts: () => api.get<{ receipts: ReceiptRow[] }>("/subscriptions/receipts"),
  preview: (
    plan?: "starter" | "pro",
    billing_cycle?: "monthly" | "annual",
    opts: { seats?: number; months_prepaid?: number; coupon_code?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    if (plan) qs.set("plan", plan);
    if (billing_cycle) qs.set("billing_cycle", billing_cycle);
    if (opts.seats != null) qs.set("seats", String(opts.seats));
    if (opts.months_prepaid != null) qs.set("months_prepaid", String(opts.months_prepaid));
    if (opts.coupon_code !== undefined) qs.set("coupon_code", opts.coupon_code);
    const q = qs.toString();
    return api.get<PreviewResponse>(`/subscriptions/preview${q ? "?" + q : ""}`);
  },
  redeemCoupon: (code: string) =>
    api.post<{ ok: true; coupon: { code: string; description: string | null; discount_pct: number | null } }>(
      "/subscriptions/redeem-coupon", { code },
    ),
  clearCoupon: () => api.delete<void>("/subscriptions/coupon"),
  changePlan: (
    plan: "starter" | "pro",
    billing_cycle: "monthly" | "annual",
    opts: { seats?: number; months_prepaid?: number } = {},
  ) =>
    api.post<{
      status: "no_op" | "charged" | "downgraded";
      subscription_id: string;
      plan: string;
      billing_cycle: string;
      proration: { days_remaining: number; net_delta_cents: string; today_charge_gross_cents: string };
    }>("/subscriptions/change-plan", {
      plan,
      billing_cycle,
      ...(opts.seats != null ? { seats: opts.seats } : {}),
      ...(opts.months_prepaid != null ? { months_prepaid: opts.months_prepaid } : {}),
    }),
  paymentMethod: () => api.get<PaymentMethodResponse>("/subscriptions/payment-method"),
  removePaymentMethod: () => api.delete<void>("/subscriptions/payment-method"),
};

// ─── Money formatting ─────────────────────────────────────────────────
export function fmtCents(cents: string | bigint | number | null | undefined, currency = "LKR"): string {
  // Guard the whole path — AI Inbox extractions sometimes surface null for
  // fields the model couldn't read (handwritten totals, missing quantity).
  // Historically callers wrapped every fmtCents with a truthy check; too
  // easy to miss one and freeze the tab (BigInt(null) → TypeError). Do it
  // once here and every caller stays safe.
  if (cents === null || cents === undefined || cents === "") return "";
  let n: bigint;
  try {
    n = typeof cents === "string" ? BigInt(cents) : typeof cents === "number" ? BigInt(Math.trunc(cents)) : cents;
  } catch {
    return "";  // unparseable — swallow rather than crash the render.
  }
  const negative = n < 0n;
  const abs = negative ? -n : n;
  const whole = abs / 100n;
  const frac = abs % 100n;
  const wholeStr = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}${currency} ${wholeStr}.${String(frac).padStart(2, "0")}`;
}
